import { HazardCategory } from "src/app/enums/hazardCategory.enum";
import { RiskRegisterGroupBy } from "src/app/enums/riskRegisterGroupBy.enum";
import { RiskRegisterViewMode } from "src/app/enums/riskRegisterViewMode.enum";
import { DisplayGridColumnSettings, GridColumnSettings } from "src/app/models/gridColumnSettings.model";
import { GridSortSettings } from "src/app/models/gridSortSettings.model";
import { GridView } from "src/app/models/gridView.model";
import { TableExtendedRiskRegister } from "src/app/models/risk-assessment.model";
import { RiskRegisterMatrixSort, RiskRegisterViewPageSettings } from "src/app/models/risk-register-matrix.model";
import { SegPageSortBy, SegPageSortDirection, SegPageSortParams, SegPageViewMode } from "src/app/models/risk-register-seg-page.model";
import { RolePermissionObjects } from "src/app/shared/constants/rolePermissionObjects.constants";
import nameOf from "src/app/shared/form/nameOf.helper";
import { mergeSavedColumns } from "src/app/shared/grid/grid-view-default.helper";
import { hasLevelKeySwap } from "./risk-register-filter.util";

export const RISK_REGISTER_VIEW_OBJECT_ID = RolePermissionObjects.RiskRegister;
export const RISK_REGISTER_AGENTS_COLUMN = "agents";
export const RISK_REGISTER_ACTION_COLUMN = "action";
export const RISK_REGISTER_AGENT_WINDOW_COLUMN = "agentWindow";
export const RISK_REGISTER_DEFAULT_FROZEN_COLUMN_COUNT = 4;

const segCodeColumn = nameOf((x: TableExtendedRiskRegister) => x.segCode);
const segNameColumn = nameOf((x: TableExtendedRiskRegister) => x.segName);
const workersColumn = nameOf((x: TableExtendedRiskRegister) => x.numberOfWorkers);
const contractorsColumn = nameOf((x: TableExtendedRiskRegister) => x.numberOfContractors);
const orgLevelColumn = nameOf((x: TableExtendedRiskRegister) => x.orgLevelName);

interface RiskRegisterViewColumnDef {
    property: string;
    displayName: string;
    locked: boolean;
    hiddenByDefault: boolean;
}

const COLUMN_DEFS: RiskRegisterViewColumnDef[] = [
    { property: segCodeColumn, displayName: "SEG Code", locked: false, hiddenByDefault: false },
    { property: segNameColumn, displayName: "SEG", locked: true, hiddenByDefault: false },
    { property: workersColumn, displayName: "No of Employees", locked: false, hiddenByDefault: false },
    { property: contractorsColumn, displayName: "No of Contractors", locked: false, hiddenByDefault: false },
    { property: orgLevelColumn, displayName: "Organization Level", locked: false, hiddenByDefault: true },
    { property: RISK_REGISTER_AGENTS_COLUMN, displayName: "Agents", locked: true, hiddenByDefault: false },
];

const hiddenAgentsKey = nameOf<RiskRegisterViewPageSettings>((x) => x.hiddenAgentIds);
const viewModeKey = nameOf<RiskRegisterViewPageSettings>((x) => x.viewMode);
const groupByKey = nameOf<RiskRegisterViewPageSettings>((x) => x.groupBy);
const disabledFiltersKey = nameOf<RiskRegisterViewPageSettings>((x) => x.disabledFilterKeys);

const COLUMN_PROPERTIES = COLUMN_DEFS.map((def) => def.property);
const LOCKED_PROPERTIES = new Set(COLUMN_DEFS.filter((def) => def.locked).map((def) => def.property));

const FIXED_SORT_BY = new Map<string, SegPageSortBy>([
    [segCodeColumn, "SegCode"],
    [segNameColumn, "SegName"],
    [workersColumn, "NumberOfWorkers"],
    [contractorsColumn, "NumberOfContractors"],
]);

const SORT_DIRECTION = new Map<string, SegPageSortDirection>([
    ["asc", "Asc"],
    ["desc", "Desc"],
]);

const VIEW_MODE_NAMES: Record<RiskRegisterViewMode, SegPageViewMode> = {
    [RiskRegisterViewMode.ExposureBand]: "ExposureBand",
    [RiskRegisterViewMode.Likelihood]: "Likelihood",
    [RiskRegisterViewMode.HealthRisk]: "HealthRisk",
};

export interface RiskRegisterViewState {
    sort: RiskRegisterMatrixSort | null;
    filters: Record<string, unknown>;
    viewMode: SegPageViewMode | null;
    groupBy: RiskRegisterGroupBy;
}

export interface SegPageSortContext {
    viewMode: RiskRegisterViewMode;
    usesHealthRiskScore: boolean;
    visibleAgentIds: ReadonlySet<string>;
}

export function riskRegisterGridId(category: HazardCategory | null): string {
    return category == null ? RISK_REGISTER_VIEW_OBJECT_ID : `${RISK_REGISTER_VIEW_OBJECT_ID}_${HazardCategory[category]}`;
}

export function defaultViewColumns(): GridColumnSettings[] {
    return COLUMN_DEFS.map((def, order) => ({ property: def.property, visible: !def.hiddenByDefault, order }));
}

function buildViewColumn(property: string, order: number, visible?: boolean): DisplayGridColumnSettings {
    const def = COLUMN_DEFS.find((item) => item.property === property);
    return {
        _uid: property,
        property,
        displayName: def?.displayName ?? property,
        visible: visible ?? !def?.hiddenByDefault,
        order,
        type: "string",
        isNullable: false,
        disabled: LOCKED_PROPERTIES.has(property),
        isDefault: !def?.hiddenByDefault,
        isSortable: false,
        isFilterable: false,
        nestedPath: null,
        isDisplayNameConfigurable: true,
    };
}

export function keepLockedColumnsVisible(columns: DisplayGridColumnSettings[]): void {
    columns.forEach((column) => {
        if (LOCKED_PROPERTIES.has(column.property)) {
            column.visible = true;
        }
    });
}

export function buildViewColumns(saved: GridColumnSettings[] | null | undefined): DisplayGridColumnSettings[] {
    const known = (saved ?? []).filter(
        (column, index, all) =>
            COLUMN_PROPERTIES.includes(column.property) && all.findIndex((item) => item.property === column.property) === index,
    );
    const columns = mergeSavedColumns(known.length ? known : defaultViewColumns(), COLUMN_PROPERTIES, buildViewColumn);
    keepLockedColumnsVisible(columns);
    return columns;
}

export function serializeViewColumns(columns: DisplayGridColumnSettings[]): GridColumnSettings[] {
    return columns.map((column) => ({ property: column.property, visible: column.visible, order: column.order }));
}

function byOrder(columns: DisplayGridColumnSettings[]): DisplayGridColumnSettings[] {
    return [...columns].sort((left, right) => left.order - right.order);
}

export function toDisplayedColumns(columns: DisplayGridColumnSettings[], agentIds: string[]): string[] {
    const names = byOrder(columns)
        .filter((column) => column.visible)
        .flatMap((column) => (column.property === RISK_REGISTER_AGENTS_COLUMN ? agentIds : [column.property]));
    return [...names, RISK_REGISTER_ACTION_COLUMN];
}

export function toRowColumns(displayedColumns: string[], agentIds: string[]): string[] {
    const agents = new Set(agentIds);
    return displayedColumns.flatMap((column) => {
        if (!agents.has(column)) {
            return [column];
        }
        return column === agentIds[0]
            ? [RISK_REGISTER_AGENT_WINDOW_COLUMN]
            : [];
    });
}

export function isGroupHeaderInCodeColumn(displayedColumns: string[]): boolean {
    const codeIndex = displayedColumns.indexOf(segCodeColumn);
    return codeIndex >= 0 && displayedColumns[codeIndex + 1] === segNameColumn;
}

export function freezableColumns(columns: DisplayGridColumnSettings[]): ReadonlySet<string> {
    const ordered = byOrder(columns).map((column) => column.property);
    const agentsIndex = ordered.indexOf(RISK_REGISTER_AGENTS_COLUMN);
    return new Set(agentsIndex < 0 ? ordered : ordered.slice(0, agentsIndex));
}

export function frozenColumns(columns: DisplayGridColumnSettings[], frozenColumnCount: number): ReadonlySet<string> {
    const frozen = new Set<string>();
    for (const column of byOrder(columns).filter((item) => item.visible).slice(0, frozenColumnCount)) {
        if (column.property === RISK_REGISTER_AGENTS_COLUMN) {
            break;
        }
        frozen.add(column.property);
    }
    return frozen;
}

export function viewModeName(mode: RiskRegisterViewMode): SegPageViewMode {
    return VIEW_MODE_NAMES[mode];
}

export function viewModeFromName(name: unknown): RiskRegisterViewMode | null {
    const entry = Object.entries(VIEW_MODE_NAMES).find(([, value]) => value === name);
    return entry ? (entry[0] as RiskRegisterViewMode) : null;
}

export function groupByFromName(name: unknown): RiskRegisterGroupBy {
    return Object.values(RiskRegisterGroupBy).find((value) => value === name) ?? RiskRegisterGroupBy.None;
}

export function readPageSettings(raw: Record<string, unknown> | null | undefined): RiskRegisterViewPageSettings {
    const hidden = raw?.[hiddenAgentsKey];
    const viewMode = raw?.[viewModeKey];
    const disabled = raw?.[disabledFiltersKey];
    return {
        hiddenAgentIds: Array.isArray(hidden) ? hidden.filter((id): id is string => typeof id === "string") : [],
        disabledFilterKeys: Array.isArray(disabled) ? disabled.filter((key): key is string => typeof key === "string") : [],
        viewMode: viewModeFromName(viewMode) ? (viewMode as SegPageViewMode) : undefined,
        groupBy: groupByFromName(raw?.[groupByKey]),
    };
}

export function writePageSettings(
    existing: Record<string, unknown> | null | undefined,
    state: {
        hiddenAgentIds: Iterable<string>;
        viewMode: RiskRegisterViewMode;
        groupBy: RiskRegisterGroupBy;
        disabledFilterKeys?: Iterable<string>;
    },
): Record<string, unknown> {
    const next: Record<string, unknown> = { ...existing, [viewModeKey]: viewModeName(state.viewMode), [groupByKey]: state.groupBy };
    const hidden = [...state.hiddenAgentIds];
    if (hidden.length) {
        next[hiddenAgentsKey] = hidden;
    } else {
        delete next[hiddenAgentsKey];
    }
    const disabled = [...(state.disabledFilterKeys ?? [])].sort();
    if (disabled.length) {
        next[disabledFiltersKey] = disabled;
    } else {
        delete next[disabledFiltersKey];
    }
    return next;
}

export function sortToSettings(sort: RiskRegisterMatrixSort | null): GridSortSettings | undefined {
    return sort && sort.direction ? { sortBy: sort.active, sortOrder: sort.direction } : undefined;
}

export function sortFromSettings(settings: GridSortSettings | null | undefined): RiskRegisterMatrixSort | null {
    if (!settings?.sortBy || !SORT_DIRECTION.has(settings.sortOrder)) {
        return null;
    }
    return { active: settings.sortBy, direction: settings.sortOrder };
}

export function isAgentColumnSort(sort: RiskRegisterMatrixSort | null): boolean {
    return !!sort?.direction && !FIXED_SORT_BY.has(sort.active);
}

export function resolveSort(
    sort: RiskRegisterMatrixSort | null,
    visibleAgentIds: ReadonlySet<string>,
): RiskRegisterMatrixSort | null {
    if (!sort || !sort.direction) {
        return null;
    }
    return FIXED_SORT_BY.has(sort.active) || visibleAgentIds.has(sort.active) ? sort : null;
}

export function toSegPageSortParams(sort: RiskRegisterMatrixSort | null, context: SegPageSortContext): SegPageSortParams {
    const direction = sort ? SORT_DIRECTION.get(sort.direction) : undefined;
    if (!sort || !direction) {
        return {};
    }
    const fixed = FIXED_SORT_BY.get(sort.active);
    if (fixed) {
        return fixed === "SegName" && direction === "Asc" ? {} : { sortBy: fixed, sortDirection: direction };
    }
    if (context.visibleAgentIds.has(sort.active)) {
        return {
            sortBy: "Agent",
            sortAgentId: sort.active,
            sortDirection: direction,
            viewMode: context.usesHealthRiskScore ? "HealthRisk" : viewModeName(context.viewMode),
        };
    }
    return {};
}

export function filtersToSettings(filters: Record<string, unknown>): Record<string, string> {
    return Object.fromEntries(
        Object.entries(filters)
            .filter(([, value]) => value != null && value !== "")
            .map(([key, value]) => [key, JSON.stringify(value)]),
    );
}

export function filtersFromSettings(filters: Record<string, string> | null | undefined): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(filters ?? {}).map(([key, value]) => {
            try {
                return [key, JSON.parse(value) as unknown];
            } catch {
                return [key, value];
            }
        }),
    );
}

export function readViewState(view: GridView | null | undefined): RiskRegisterViewState {
    const settings = view?.settingsJson;
    const page = readPageSettings(settings?.pageSettings);
    return {
        sort: sortFromSettings(settings?.sort),
        filters: filtersFromSettings(settings?.filters),
        viewMode: page.viewMode ?? null,
        groupBy: page.groupBy ?? RiskRegisterGroupBy.None,
    };
}

function stableJson(value: unknown): string {
    return JSON.stringify(value, (_, nested: unknown) =>
        nested && typeof nested === "object" && !Array.isArray(nested)
            ? Object.fromEntries(Object.entries(nested).sort(([left], [right]) => left.localeCompare(right)))
            : nested,
    );
}

export function needsReload(
    previous: RiskRegisterViewState,
    next: RiskRegisterViewState,
    agentIds: ReadonlySet<string>,
    viewModeAffectsSort = true,
): boolean {
    const agentSorted = !!next.sort && agentIds.has(next.sort.active);
    const exposureBand = viewModeName(RiskRegisterViewMode.ExposureBand);
    return (
        stableJson(previous.sort) !== stableJson(next.sort) ||
        stableJson(previous.filters) !== stableJson(next.filters) ||
        previous.groupBy !== next.groupBy ||
        (viewModeAffectsSort && agentSorted && previous.viewMode !== next.viewMode) ||
        (viewModeAffectsSort && hasLevelKeySwap(next.filters, previous.viewMode === exposureBand, next.viewMode === exposureBand))
    );
}
